import {
    AsOfBadge,
    DateTime,
    type ListColumnDef,
    PageHeader,
    PublicId,
    StatusBadge,
} from '@bemor/web-kit/ui';

import type { StaffOrganizationRow } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';

const PATH = '/staff/organizations';
const PAGE_KEY = 'organizations';

/** BM-93 (design 0.9.0 `backoffice/organizations`): every organization of the tenant, read-only but the lifecycle. */
export const organizationColumns: ListColumnDef<StaffOrganizationRow>[] = [
    {
        key: 'name',
        label: 'Organization',
        sortKey: 'name',
        cell: (o) => (
            <span className="font-medium">
                {o.name || (
                    <span className="text-muted-foreground">
                        Unnamed organization
                    </span>
                )}
            </span>
        ),
        csv: (o) => o.name,
        locked: 'start',
    },
    {
        key: 'id',
        label: 'Id',
        cell: (o) => <PublicId id={o.id} />,
        csv: (o) => o.id,
    },
    {
        key: 'owner',
        label: 'Owner',
        cell: (o) =>
            o.owner ? (
                <span className="flex min-w-0 flex-col">
                    <span>{o.owner.name}</span>
                    <span className="text-muted-foreground truncate text-xs">
                        {o.owner.email}
                    </span>
                </span>
            ) : (
                <span className="text-muted-foreground">—</span>
            ),
        csv: (o) => o.owner?.email ?? null,
    },
    {
        key: 'merchants_count',
        label: 'Merchants',
        cell: (o) => o.merchants_count,
        csv: (o) => o.merchants_count,
        noTruncate: true,
    },
    {
        key: 'members_count',
        label: 'Members',
        cell: (o) => o.members_count,
        csv: (o) => o.members_count,
        noTruncate: true,
    },
    {
        key: 'status',
        label: 'Status',
        sortKey: 'status',
        cell: (o) => <StatusBadge status={o.status} domain="organization" />,
        csv: (o) => o.status,
        groupable: { key: 'status', statusDomain: 'organization' },
    },
    {
        key: 'created_at',
        aliases: ['created'],
        label: 'Since',
        kind: 'datetime',
        sortKey: 'created_at',
        cell: (o) => <DateTime value={o.created_at} format="date" empty="—" />,
        csv: (o) => o.created_at,
        noTruncate: true,
    },
];

export const organizationFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    range: 'Created',
});

export const organizationsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

export function OrganizationsPage() {
    const { state, query } = useAdminList<StaffOrganizationRow>(
        PAGE_KEY,
        PATH,
        { columns: organizationColumns, filters: organizationFilters },
    );
    return (
        <div>
            <PageHeader
                title="Organizations"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'The customers above the merchants: who signed up, their merchants, stores and people. Read-only; suspend from the detail page.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="organizations"
                state={state}
                query={query}
                rowKey={(o) => o.id}
                rowHref={(o) => `/organizations/${o.id}`}
                controls={{
                    q: { kind: 'search' },
                    status: { domain: 'organization' },
                }}
            />
        </div>
    );
}
